import React from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import routes from "routes.js";
import { sessionManager } from "utils/sessionManager";
import toast, { Toaster } from "react-hot-toast";

export default function Admin(props) {
  const { ...rest } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(window.innerWidth >= 1200);
  const [currentRoute, setCurrentRoute] = React.useState("RCN Dashboard");

  // Check if current route is ZoneDetails
  const isZoneDetails = location.pathname.includes('/warehouse/zone-details/') || location.pathname.includes('/finished-products/zones/');
  // Public calibrated detail page should be accessible without session
  const isCalibratedDetail = location.pathname.includes('/warehouse/calibrated/');
  
  // Debug logging
  console.log('Current pathname:', location.pathname);
  console.log('Is zone details page:', isZoneDetails);

  // Session management
  React.useEffect(() => {
    // Skip ALL session checks for Zone Details pages and calibrated detail page
    if (isZoneDetails || isCalibratedDetail) {
      console.log('Skipping session checks for zone details or calibrated detail page');
      return;
    }

    // Check if user is logged in
    if (!sessionManager.isSessionValid()) {
      console.log('Session not valid, redirecting to sign-in');
      toast.error('Session expired. Please login again.');
      navigate('/auth/sign-in');
      return;
    }

    // Set up automatic session checking
    const cleanup = sessionManager.setupSessionCheck(() => {
      console.log('Session expired due to inactivity, redirecting to sign-in');
      toast.error('Session expired due to inactivity. Please login again.');
      navigate('/auth/sign-in');
    });

    return cleanup;
  }, [navigate, isZoneDetails, location.pathname]);

  React.useEffect(() => {
    const handleResize = () => {
      if (isZoneDetails) {
        setOpen(false);
      } else {
        setOpen(window.innerWidth >= 1200);
      }
    };

    window.addEventListener("resize", handleResize);
    // Initial check
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [isZoneDetails]);

  React.useEffect(() => {
    updateActiveRoute();
  }, [location.pathname]);

  const updateActiveRoute = () => {
    const activeRouteName = findActiveRoute(routes) || "Main Dashboard";
    setCurrentRoute(activeRouteName);
    return activeRouteName;
  };

  const findActiveRoute = (routeList) => {
    for (let i = 0; i < routeList.length; i++) {
      const route = routeList[i];
      if (route.layout && route.path) {
        if (window.location.href.indexOf(route.layout + "/" + route.path) !== -1) {
          return route.name || null;
        }
      }
      if (route.submenu) {
        const childActive = findActiveRoute(route.submenu);
        if (childActive) {
          return childActive;
        }
      }
    }
    return null;
  };

  const getActiveNavbar = (routeList) => {
    for (let i = 0; i < routeList.length; i++) {
      const route = routeList[i];
      if (route.layout && route.path) {
        if (window.location.href.indexOf(route.layout + route.path) !== -1) {
          return route.secondary;
        }
      }
      if (route.submenu) {
        const childNavbar = getActiveNavbar(route.submenu);
        if (childNavbar) {
          return childNavbar;
        }
      }
    }
    return false;
  };

  const getRoutes = () => {
    return buildRoutes(routes);
  };

  const buildRoutes = (routeList) => {
    let collectedRoutes = [];

    routeList.forEach((route) => {
      if (route.path && route.component) {
        collectedRoutes.push(
          <Route path={route.path} element={route.component} key={route.path} />
        );
      }

      if (route.submenu) {
        collectedRoutes = collectedRoutes.concat(buildRoutes(route.submenu));
      }
    });

    return collectedRoutes;
  };

  document.documentElement.dir = "ltr";
  return (
    <div className="flex h-full w-full">
      <Toaster position="top-right" />
      {!isZoneDetails && <Sidebar open={open} onClose={() => setOpen(false)} />}
      {/* Navbar & Main Content */}
      <div className="h-full w-full bg-lightPrimary dark:!bg-navy-900">
        {/* Main Content */}
        <main
          className={`mx-[12px] h-full flex-none transition-all md:pr-2 ${
            !isZoneDetails ? 'xl:ml-[332px]' : ''
          }`}
        >
          {/* Routes */}
          <div className="h-full">
            <Navbar
              onOpenSidenav={() => setOpen(true)}
              logoText={"Horizon UI Tailwind React"}
              brandText={currentRoute}
              secondary={getActiveNavbar(routes)}
              hideRegistrationAndProfile={isZoneDetails || isCalibratedDetail}
              hideLogout={isCalibratedDetail}
              {...rest}
            />
            <div className="pt-5s mx-auto mb-auto h-full min-h-[84vh] p-2 md:pr-2">
              <Routes>
                {getRoutes()}
                <Route path="/" element={<Navigate to="/admin/warehouse/dashboard" replace />} />
              </Routes>
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
